<script setup lang="ts">
import type { Segment } from '@optionway/models';
import { Clock, List, Plane, TicketsPlane } from 'lucide-vue-next';

const selectedSegment = defineProps<Segment>();

const prefix = 'components.results.segment';

const { locale } = useI18n();

const locationLabelsStore = useLocationLabels();
</script>

<template>
  <div class="flex flex-col gap-3">
    <!-- Header: airline + compact info row -->
    <div class="flex items-center gap-3">
      <UiCustomAirlineLogo
        v-if="selectedSegment.marketingCarrier"
        :iata-code="selectedSegment.marketingCarrier.iataCode"
        :name="selectedSegment.marketingCarrier.name"
        :small="true"
        class="h-7 w-auto max-w-14 shrink-0"
      />
      <div class="flex flex-col gap-0.5">
        <div class="flex flex-wrap items-center gap-x-1 gap-y-0">
          <span class="inline-flex items-center gap-1 text-xs leading-5 text-text-secondary-600">
            <TicketsPlane
              :size="12"
              class="shrink-0"
              aria-hidden="true"
            />
            {{ selectedSegment.marketingCarrier?.name
            }}<template
              v-if="
                selectedSegment.operatingCarrier?.iataCode &&
                selectedSegment.operatingCarrier.iataCode !== selectedSegment.marketingCarrier?.iataCode
              "
            >
              &nbsp;{{ $t(`${prefix}.operatedBy`) }} {{ selectedSegment.operatingCarrier.name }}</template
            >
          </span>
          <div class="h-1 w-1 shrink-0 rounded-full bg-gray-400" />
          <span class="inline-flex items-center gap-1 whitespace-nowrap text-xs leading-5 text-text-secondary-600">
            <List
              :size="12"
              class="shrink-0"
              aria-hidden="true"
            />
            {{ useUpperCase(selectedSegment.flightNumber?.iataCode ?? '') }}
            {{ selectedSegment.flightNumber?.number }}
          </span>
          <template v-if="selectedSegment.cabinClass">
            <div class="h-1 w-1 shrink-0 rounded-full bg-gray-400" />
            <span class="inline-flex items-center gap-1 text-xs leading-5 text-text-secondary-600">
              <SvgoResultsSegmentCabinClass
                class="h-3 w-3 shrink-0"
                :font-controlled="false"
              />
              <i18n-t
                scope="global"
                :keypath="`${prefix}.cabinClass`"
                tag="span"
              >
                <template #cabinClass>
                  {{ $t(`components.search.cabinClassSelector.${selectedSegment.cabinClass}`) }}
                </template>
              </i18n-t>
            </span>
          </template>
          <template v-if="selectedSegment.equipment?.name || selectedSegment.equipment?.iataCode">
            <div class="h-1 w-1 shrink-0 rounded-full bg-gray-400" />
            <span class="inline-flex items-center gap-1 text-xs leading-5 text-text-secondary-600">
              <Plane
                :size="12"
                class="shrink-0"
                aria-hidden="true"
              />
              {{ selectedSegment.equipment?.name }}
              {{ selectedSegment.equipment?.iataCode }}
            </span>
          </template>
        </div>
      </div>
    </div>

    <div class="flex items-stretch gap-3 py-1">
      <!-- Heures (à gauche de la timeline) -->
      <div class="flex w-20 shrink-0 flex-col items-end justify-between">
        <span class="flex flex-col items-center">
          <span class="whitespace-nowrap text-xs font-normal text-text-secondary-600">
            {{ useDateFormat(selectedSegment.departureDateTime, 'ddd DD/MM', { locales: locale }).value }}
          </span>

          <span>
            <span class="text-sm font-semibold md:text-base">
              {{ useDateFormat(selectedSegment.departureDateTime, 'HH:mm') }} </span
            ><sup
              v-if="selectedSegment.departureDayOffset"
              class="text-xs font-normal text-red-500"
              >+{{ selectedSegment.departureDayOffset }}
            </sup>
          </span>
        </span>
        <span class="flex flex-col items-center">
          <span class="whitespace-nowrap text-xs font-normal text-text-secondary-600">
            {{ useDateFormat(selectedSegment.arrivalDateTime, 'ddd DD/MM', { locales: locale }).value }}
          </span>
          <span
            ><span class="text-sm font-semibold md:text-base">{{
              useDateFormat(selectedSegment.arrivalDateTime, 'HH:mm')
            }}</span
            ><sup
              v-if="selectedSegment.arrivalDayOffset"
              class="text-xs font-normal text-red-500"
              >+{{ selectedSegment.arrivalDayOffset }}</sup
            ></span
          >
        </span>
      </div>

      <!-- Timeline -->
      <div class="flex w-3 flex-col items-center md:w-6">
        <div
          class="h-3 w-3 shrink-0 rounded-full border bg-background"
          :class="
            selectedSegment.departure?.displayType === 'WARNING'
              ? 'border-orange-700'
              : selectedSegment.equipment?.type !== 'PLANE'
                ? 'border-dark-blue-700'
                : 'border-gray-400'
          "
        />
        <template v-if="selectedSegment.equipment?.type === 'BUS'">
          <div class="flex-1 border-l border-dark-blue-700" />
          <SvgoResultsSegmentBus
            class="my-0.5 h-4 w-4 shrink-0 text-dark-blue-700"
            filled
            :font-controlled="false"
          />
          <div class="flex-1 border-l border-dark-blue-700" />
        </template>
        <template v-else-if="selectedSegment.equipment?.type === 'TRAIN'">
          <div class="flex-1 border-l border-dark-blue-700" />
          <SvgoResultsSegmentTrain
            class="my-0.5 h-4 w-4 shrink-0 text-dark-blue-700"
            filled
            :font-controlled="false"
          />
          <div class="flex-1 border-l border-dark-blue-700" />
        </template>
        <template v-else-if="selectedSegment.withTechnical">
          <div class="flex-1 border-l border-gray-300" />
          <div class="h-2 w-2 shrink-0 rounded-full border border-gray-400 bg-background" />
          <div class="flex-1 border-l border-gray-300" />
        </template>
        <template v-else>
          <div class="flex-1 border-l border-gray-300" />
        </template>
        <div
          class="h-3 w-3 shrink-0 rounded-full border bg-background"
          :class="
            selectedSegment.arrival?.displayType === 'WARNING'
              ? 'border-orange-700'
              : selectedSegment.equipment?.type !== 'PLANE'
                ? 'border-dark-blue-700'
                : 'border-gray-400'
          "
        />
      </div>

      <!-- Détails itinéraire -->
      <div class="flex flex-1 flex-col gap-5">
        <!-- Départ -->
        <span
          class="min-w-0 text-sm text-text-secondary-600"
          :class="{ 'text-orange-700': selectedSegment.departure?.displayType === 'WARNING' }"
        >
          {{ locationLabelsStore.airport(selectedSegment.departure?.iataCode) }}
          <span
            :class="
              selectedSegment.departure?.displayType === 'WARNING' ? 'text-orange-700' : 'text-text-secondary-600'
            "
          >
            ({{ selectedSegment.departure?.iataCode }})
          </span>
          <template v-if="selectedSegment.departure?.terminal">
            ({{ $t(`${prefix}.terminal`) }} {{ selectedSegment.departure.terminal }})</template
          >,
          {{ locationLabelsStore.country(selectedSegment.departure?.countryIataCode) }}
        </span>

        <!-- Badge durée -->
        <UiCustomBadge :variant="selectedSegment.equipment?.groundTransport ? 'dark-blue' : 'gray'">
          <Clock
            :size="12"
            aria-hidden="true"
          />
          {{ useDurationFormat(selectedSegment.duration) }}
          <template v-if="selectedSegment.equipment?.type === 'BUS'"> · {{ $t(`${prefix}.byBus`) }} </template>
          <template v-else-if="selectedSegment.equipment?.type === 'TRAIN'"> · {{ $t(`${prefix}.byTrain`) }} </template>
          <template v-else-if="selectedSegment.withTechnical">
            ·
            <i18n-t
              v-if="
                selectedSegment.previousCorrespondence?.duration &&
                selectedSegment.previousCorrespondence?.location?.iataCode
              "
              scope="global"
              :keypath="`${prefix}.withTechnicalDetail`"
              tag="span"
            >
              <template #duration>{{ useDurationFormat(selectedSegment.previousCorrespondence.duration) }}</template>
              <template #airport>{{
                locationLabelsStore.airport(selectedSegment.previousCorrespondence.location.iataCode)
              }}</template>
              <template #iataCode>{{ selectedSegment.previousCorrespondence.location.iataCode }}</template>
            </i18n-t>
            <template v-else>{{ $t(`${prefix}.withTechnical`) }}</template>
          </template>
        </UiCustomBadge>

        <!-- Arrivée -->
        <span
          class="min-w-0 text-sm text-text-secondary-600"
          :class="{ 'text-orange-700': selectedSegment.arrival?.displayType === 'WARNING' }"
        >
          {{ locationLabelsStore.airport(selectedSegment.arrival?.iataCode) }}
          <span
            :class="selectedSegment.arrival?.displayType === 'WARNING' ? 'text-orange-700' : 'text-text-secondary-600'"
          >
            ({{ selectedSegment.arrival?.iataCode }})
          </span>
          <template v-if="selectedSegment.arrival?.terminal">
            ({{ $t(`${prefix}.terminal`) }} {{ selectedSegment.arrival.terminal }})</template
          >,
          {{ locationLabelsStore.country(selectedSegment.arrival?.countryIataCode) }}
        </span>
      </div>
    </div>
  </div>

  <!-- Alerte décalage jour -->
  <div
    v-if="selectedSegment.last && selectedSegment.arrivalDayOffset"
    class="flex items-center gap-2 rounded-xl border border-orange-200 bg-orange-50 px-3 py-2"
  >
    <SvgoResultsSearchResultAlertCircle
      class="h-4 w-4 shrink-0 text-orange-700"
      filled
      :font-controlled="false"
    />
    <span class="text-sm font-normal text-foreground">
      {{ $t(`${prefix}.dayOffset`, selectedSegment.arrivalDayOffset) }}
    </span>
  </div>
</template>
